/**
 * Form helpers for create/edit sport club views.
 */
import type {
  CreateSportClubDto,
  SportClub,
  SportClubFormValues,
  UpdateSportClubDto,
} from '@/types/sport-club.types';
import type { AddressSelectionResult } from '@/types/geography.types';
import { resolveStoredDepartmentCode } from '@/utils/department-lookup-utils';
import { getCompanyCountry } from '@/stores/themeStore';

/** Empty string → omit from the DTO. */
function trimOrUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed || undefined;
}

/** Empty create form; edit hydrates later via {@link sportClubToFormData}. */
export function emptyCreateSportClubForm(): SportClubFormValues {
  return {
    name: '',
    sportType: '',
    description: '',
    address: '',
    zipCode: '',
    city: '',
    country: getCompanyCountry(),
    department: '',
    website: '',
    contactEmail: '',
    contactPhone: '',
  };
}

/** Hydrate the form from GET sport club. {@link Select} uses a department code. */
export function sportClubToFormData(club: SportClub): SportClubFormValues {
  return {
    name: club.name || '',
    sportType: club.sportType || '',
    description: club.description || '',
    address: club.address || '',
    zipCode: club.zipCode || '',
    city: club.city || '',
    country: club.country || getCompanyCountry(),
    department: resolveStoredDepartmentCode(club.department, club.zipCode),
    website: club.website || '',
    contactEmail: club.contactEmail || '',
    contactPhone: club.contactPhone || '',
  };
}

/** Patch one string field. */
export function applySportClubFormField(
  form: SportClubFormValues,
  field: keyof SportClubFormValues,
  value: string,
): SportClubFormValues {
  return { ...form, [field]: value };
}

/** Fill address / city / department from an autocomplete pick. */
export function applySportClubAddressSelection(
  form: SportClubFormValues,
  result: AddressSelectionResult,
): SportClubFormValues {
  return {
    ...form,
    address: result.addressLine1,
    zipCode: result.postalCode || '',
    city: result.city || '',
    country: result.country || getCompanyCountry(),
    department: result.departmentCode || '',
  };
}

/** Shared create/update payload; blank optional fields are omitted. */
function buildSportClubPayload(form: SportClubFormValues): CreateSportClubDto {
  return {
    name: form.name.trim(),
    sportType: trimOrUndefined(form.sportType),
    description: trimOrUndefined(form.description),
    address: trimOrUndefined(form.address),
    zipCode: trimOrUndefined(form.zipCode),
    city: trimOrUndefined(form.city),
    country: trimOrUndefined(form.country),
    department: trimOrUndefined(form.department),
    website: trimOrUndefined(form.website),
    contactEmail: trimOrUndefined(form.contactEmail),
    contactPhone: trimOrUndefined(form.contactPhone),
  };
}

/** Create DTO (same payload as update). */
export function buildCreateSportClubDto(form: SportClubFormValues): CreateSportClubDto {
  return buildSportClubPayload(form);
}

/** Update DTO (same payload as create). */
export function buildUpdateSportClubDto(form: SportClubFormValues): UpdateSportClubDto {
  return buildSportClubPayload(form);
}

/** Name is the only required field. */
export function isSportClubFormValid(form: SportClubFormValues): boolean {
  return !!form.name.trim();
}
